import { animation, AnimationReferenceMetadata, useAnimation } from '@angular/animations';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { LanguageService } from '@cutting/main/shared/services/language.service';
import { LogoutService } from '@cutting/main/shared/services/logout.service';
import { NotificationService } from '@cutting/main/shared/services/notification.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { navigation } from '@cutting/navigation/navigation';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { CuttingClientIdentification, CuttingSettings, LocalizationHelper } from 'cloud-shared-lib';
import { swing } from 'projects/cloud-shared-lib/src/lib/animation/swing.animation';
import { from, Observable, Subject } from 'rxjs';
import { concatMap, filter, takeUntil, tap } from 'rxjs/operators';

import { Platform } from '@angular/cdk/platform';
import { environment } from '../../../../../src/environments/environment';

interface Shortcut {
  title: string;
  icon: string;
  url: string;
}

@Component({
  selector: 'cutting-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  animInterval = 10000;
  animeteStop = true;

  horizontalNavbar: boolean;
  rightNavbar: boolean;
  animationState = false;
  hiddenNavbar: boolean;
  languages: any;
  navigation: any;
  userStatusOptions: any[];
  howToOrderUrl: string;
  markAsRead$: Observable<void>;
  isMobile = false;

  notificationsCount = 0;
  notifyConnection$: Observable<void>;
  cuttingSettings$: Observable<CuttingSettings>;

  // Private
  private unsubscribeAll: Subject<any>;

  /**
   * Constructor
   * @param fuseConfigService
   * @param fuseSidebarService
   * @param logoutService
   * @param router
   * @param languageService
   * @param notificationService
   * @param settingsService
   * @param platform
   * @param locale
   */
  constructor(
    private fuseConfigService: FuseConfigService,
    private fuseSidebarService: FuseSidebarService,
    private logoutService: LogoutService,
    private router: Router,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private settingsService: UserSettingsService,
    private platform: Platform,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.isMobile = this.platform.ANDROID || this.platform.IOS;

    this.notifyConnection$ = this.notificationService.connected$.pipe(
      filter(connected => connected),
      tap(() => {
        this.notificationService.connection.on(
          this.notificationService.hasCommonNotifications,
          (notificationsCount: number, userId: number) => {
            if (this.client.id === userId) {
              this.notificationsCount = notificationsCount;
            }
            if (notificationsCount === 0) {
              this.animeteStop = true;
            } else {
              this.animeteStop = false;
            }
          }
        );
      }),
      concatMap(() => from(this.notificationService.updateHasCommonNotifications()))
    );

    this.languages = LocalizationHelper.getLanguageList;

    this.cuttingSettings$ = this.settingsService.getCuttingSettings().pipe(
      tap(res => {
        if (!res.howToOrderUrl) {
          res.howToOrderUrl = 'https://video-share.bazissoft.ru/video/show/sWFxlQTrazEvIjKYhbwXyNAGRHBmitoJLSZnUupq';
        }
      })
    );

    this.navigation = navigation;

    // Set the private defaults
    this.unsubscribeAll = new Subject();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the config changes
    this.fuseConfigService.config.pipe(takeUntil(this.unsubscribeAll)).subscribe((settings: any) => {
      this.horizontalNavbar = settings.layout.navbar.position === 'top';
      this.rightNavbar = settings.layout.navbar.position === 'right';
      this.hiddenNavbar = settings.layout.navbar.hidden === true;
    });
    // Set the selected language from default languages
    // this.selectedLanguage = _.find(this.languages, { id: this._translateService.currentLang });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   * @param key
   */
  toggleSidebarOpen(key: any): void {
    this.fuseSidebarService.getSidebar(key).toggleOpen();

    if (this.fuseSidebarService.getSidebar(key).opened) {
      const timerForReadingNotification = setTimeout(() => {
        if (this.fuseSidebarService.getSidebar(key).opened && this.notificationsCount > 0)
          this.markAsRead$ = this.notificationService.markAllAsRead();
        clearTimeout(timerForReadingNotification);
      }, 5000);
    }
  }

  /**
   * Search
   * @param value
   */
  search(value: any): void {
    // Do your search here...
    console.log(value);
  }

  getSwingMetadata(): AnimationReferenceMetadata {
    return animation(useAnimation(swing(), { params: { delay: 1000, duration: 1000 } }));
  }

  /**
   * Set the language
   * @param lang
   */
  changeLanguage(lang: string): void {
    if (this.isDevMode()) {
      location.reload();
    } else {
      this.languageService
        .changeLanguage(lang)
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe(() => {
          this.saveLangToLS(lang);
          window.location.href = window.location.origin + '/cutting/' + lang + '/#' + this.getCurrentRoute();
        });
    }
  }

  get client(): CuttingClientIdentification {
    return LocalStorageManager.client;
  }
  set client(value: CuttingClientIdentification) {
    LocalStorageManager.client = value;
  }

  logout(): void {
    this.logoutService.logout();
  }

  getCurrentRoute(): string {
    return this.router.url;
  }

  isDevMode(): boolean {
    return !environment.production;
  }

  getShortcuts(): Readonly<Shortcut>[] {
    return [
      {
        title: $localize`Заказы`,
        icon: 'shopping_cart',
        url: '/order/list',
      },
      {
        title: $localize`Архив заказов`,
        icon: 'work',
        url: '/order/list/archive',
      },
    ];
  }

  trackFnByShortcut(index: number, item: Shortcut): string {
    return item.url;
  }

  private saveLangToLS(lang: string): void {
    const client = this.client;
    client.languageCode = lang;
    this.client = client;
    LocalStorageManager.currentLang = lang;
  }
}
